import React, { useEffect, useMemo, useState } from 'react';
import {
  FormattedMessage,
} from 'react-intl';
import escapeStringRegexp from 'escape-string-regexp';
import Modal from 'components/Modal';
import LanguageSelect from 'components/LanguageSelect';
import { Button, Checkbox, Col, Input, Row, Select, Space } from 'antd';
import globalMessages from 'translations/messages';

import { useQuery, useMutation } from 'hooks/apollo';
import { useDynamicList, useTextSelection } from 'ahooks';
import Table from 'components/Table';
import { DeleteFilled, EnterOutlined } from '@ant-design/icons';
import messages from './messages';
import './redact-personal-info.less';
import FLAGGED_COMMENT_QUERY from './flaggedComment.gql';
import CHANGE_LANGUAGE_MUTATION from './updateFlaggedCommentLanguage.gql';
import REDACT_MUTATION from './redactQuestionResponseMutation.gql';

const typeOptions = [{
  value: 'name',
  label: 'Name',
}, {
  value: 'email',
  label: 'Email',
}, {
  value: 'number',
  label: 'Number',
}, {
  value: 'licenseplate',
  label: 'License plate',
}, {
  value: 'other',
  label: 'Other',
}];

const actionOptions = [{
  value: 'anonymize',
  label: 'Anonymize ',
}, {
  value: 'allow',
  label: 'Allow',
}];

const getReplaceRegex = (text) => new RegExp(`(^|\\W)${escapeStringRegexp(text)}(\\W|$)`, 'ig');

const RedactPersonalInfo = ({ id, onDone }) => {
  const { data, loading } = useQuery(FLAGGED_COMMENT_QUERY, { variables: { id } });
  const [redact, { loading: redactLoading }] = useMutation(REDACT_MUTATION);
  const [changeLanguage] = useMutation(CHANGE_LANGUAGE_MUTATION);
  const [addEditText, setAddEditText] = useState('');
  const textSelection = useTextSelection(() => document.getElementById('response-text'));
  const addEditInput = React.useRef(null);

  const edits = useDynamicList([]);

  useEffect(() => {
    if (textSelection.text !== '') {
      setAddEditText(textSelection.text);
      addEditInput.current.focus();
    }
  }, [textSelection.text]);

  const { flaggedComment } = data || {};

  useEffect(() => {
    if (flaggedComment) {
      edits.resetList(flaggedComment.meta.map(meta => ({ id: meta.id, text: meta.value, type: meta.type, action: 'anonymize', remember: false })));
    }
  }, [flaggedComment]);

  const getEditedText = (replacement = '{{$type}}') => edits.list.filter(edit => edit.action === 'anonymize')
    .reduce((text, edit) => text.replaceAll(getReplaceRegex(edit.text.trim()), `$1${replacement.replace('$type', edit.type)}$2`), flaggedComment?.questionResponse.text);

  const onSubmit = async (next = false) => {
    const variables = {
      blacklist: edits.list.filter(edit => edit.action === 'anonymize' && edit.remember).map(edit => edit.text),
      whitelist: edits.list.filter(edit => edit.action === 'allow' && edit.remember).map(edit => edit.text),
      language: flaggedComment.language,
      text: getEditedText(),
      questionResponseId: flaggedComment.questionResponse.id,
    };

    await redact({ variables });

    onDone(next);
  };

  const onAddEdit = () => {
    const text = addEditText.trim();
    if (text?.length) {
      edits.push({ id: edits.length, text, type: 'name', action: 'anonymize' });
    }
    setAddEditText('');
  };

  const footer = (
    <Space>
      <Button onClick={() => onDone()}>
        <FormattedMessage {...globalMessages.cancel} />
      </Button>
      <Button onClick={() => onDone(true)}>
        <FormattedMessage {...globalMessages.next} />
      </Button>
      <Button type="primary" onClick={() => onSubmit(false)} loading={redactLoading}>
        <FormattedMessage {...globalMessages.save} />
      </Button>
      <Button type="primary" onClick={() => onSubmit(true)} loading={redactLoading}>
        <FormattedMessage {...messages.saveAndNext} />
      </Button>
    </Space>
  );

  const originalText = useMemo(() => flaggedComment?.meta.reduce((text, meta) => text.replaceAll(getReplaceRegex(meta.value), `$1<b class="personal-info--recommendation">${meta.value}</b>$2`), flaggedComment?.questionResponse.text), [flaggedComment]);

  const columns = [{
    dataIndex: 'text',
    title: 'Text',
    render: (text, record, index) => <Input value={text} onChange={e => edits.replace(index, { ...record, text: e.target.value })} />,
  }, {
    dataIndex: 'type',
    title: 'Type',
    width: 100,
    render: (type, record, index) => <Select options={typeOptions} value={type} onChange={(v) => edits.replace(index, { ...record, type: v })} />,
  }, {
    dataIndex: 'action',
    title: 'Action',
    width: 140,
    render: (action, record, index) => <Select options={actionOptions} value={action} onChange={(v) => edits.replace(index, { ...record, action: v })} />,
  }, {
    dataIndex: 'remember',
    title: 'Remember',
    width: 100,
    align: 'center',
    render: (remember, record, index) => <Checkbox checked={remember} onChange={e => edits.replace(index, { ...record, remember: e.target.checked })} />,
  }];

  const actions = (record, index) => [
    <Button danger key="delete" icon={<DeleteFilled />} onClick={() => { edits.remove(index); }} />,
  ];

  return (
    <Modal
      open
      fullWidth
      title={<FormattedMessage {...messages.formTitle} />}
      footer={footer}
      onCancel={() => onDone()}
      loading={loading}
    >
      {flaggedComment && (
        <Row gutter={16}>
          <Col span={12}>
            <div>
              <Space>
                <FormattedMessage {...messages.detectedLanguage} />
                <LanguageSelect allowOther value={flaggedComment.language} onChange={(language) => changeLanguage({ variables: { language, flaggedCommentId: id } })} locale={false} />
              </Space>
            </div>
            <div>
              <b><FormattedMessage {...globalMessages.customer} /></b>
              <p>{flaggedComment.questionResponse.surveyResponse.survey.customer.name}</p>
            </div>
            <div>
              <b><FormattedMessage {...globalMessages.question} /></b>
              <p dangerouslySetInnerHTML={{ __html: flaggedComment.questionResponse.question.title }} />
            </div>
            <div>
              <b><FormattedMessage {...messages.originalText} /></b>
              <p id="response-text" dangerouslySetInnerHTML={{ __html: originalText }} />
            </div>

            <div>
              <b><FormattedMessage {...messages.editedText} /></b>
              <p dangerouslySetInnerHTML={{ __html: getEditedText('<b class="personal-info--replacement">$type</b>') }} />
            </div>
          </Col>
          <Col span={12}>
            <b>Edits</b>
            <Table dataSource={edits.list} columns={columns} actions={actions} pagination={false} size="small" />
            <div style={{ marginTop: 16 }}>
              <Input placeholder="New edit" value={addEditText} onChange={e => setAddEditText(e.target.value)} onPressEnter={onAddEdit} suffix={<EnterOutlined />} ref={addEditInput} />
            </div>
          </Col>
        </Row>
      )}
    </Modal>
  );
};

export default RedactPersonalInfo;
